import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Users, BarChart2, Settings, AlertTriangle, Database, ToggleRight, CheckCircle, XCircle, Video, MessageSquare, Star, User } from 'lucide-react';
import { db, getAllFeatureSettings, updateFeatureStatus } from '../services/firebase';
import { collection, query, getDocs, orderBy, limit, getCountFromServer } from 'firebase/firestore';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [featureSettings, setFeatureSettings] = useState({});
  const [updatingFeature, setUpdatingFeature] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    toolUsage: {
      youtubeReel: 0,
    }
  });

  // Handle feature toggle
  const handleFeatureToggle = async (featureId, enabled) => {
    try {
      setUpdatingFeature(featureId);
      await updateFeatureStatus(featureId, enabled);
      
      // Update local state
      setFeatureSettings(prev => ({
        ...prev,
        [featureId]: {
          ...prev[featureId],
          enabled
        }
      }));
    } catch (error) {
      console.error(`Error toggling feature ${featureId}:`, error);
      alert('Failed to update feature status. Please try again.');
    } finally {
      setUpdatingFeature(null);
    }
  };
  
  // Fetch admin data from Firestore
  const fetchAdminData = async () => {
    try {
      setLoading(true);
      
      // Fetch feature settings
      const settings = await getAllFeatureSettings();
      setFeatureSettings(settings);
      
      // Fetch users from Firestore
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const usersList = [];
      
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        usersList.push({
          id: doc.id,
          name: userData.displayName || 'Anonymous User',
          email: userData.email || 'No Email',
          createdAt: userData.createdAt?.toDate() || new Date(),
          lastLogin: userData.lastLogin?.toDate() || new Date(),
          tools: userData.tools || []
        });
      });
      
      setUsers(usersList);
      
      // Get tool usage statistics
      const userActivityRef = collection(db, 'userActivity');
      const youtubeReelUsageQuery = query(
        userActivityRef,
        orderBy('timestamp', 'desc')
      );
      
      // Count total users and active users
      const totalUsersSnapshot = await getCountFromServer(usersRef);
      const totalUsers = totalUsersSnapshot.data().count;
      
      // Count users who logged in within the last 7 days as active
      const activeUsers = usersList.filter(user => {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        return user.lastLogin >= oneWeekAgo;
      }).length;
      
      // Get tool usage counts
      const activitySnapshot = await getDocs(youtubeReelUsageQuery);
      const youtubeReelUsageCount = activitySnapshot.docs.filter(
        doc => doc.data().toolId === 'youtube-reel'
      ).length;
      
      setStats({
        totalUsers,
        activeUsers,
        toolUsage: {
          youtubeReel: youtubeReelUsageCount
        }
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      setLoading(false);
    }
  };

  // Check if current user is admin
  useEffect(() => {
    if (!currentUser || currentUser.email !== 'kangleicode2025@gmail.com') {
      navigate('/dashboard');
    } else {
      // Fetch admin data
      fetchAdminData();
    }
  }, [currentUser, navigate]);

  // Render users tab
  const renderUsersTab = () => {
    if (loading) {
      return (
        <div className="text-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user data...</p>
        </div>
      );
    }

    return (
      <div>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {users.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tools Used
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {user.createdAt.toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {user.lastLogin.toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {user.tools.map(tool => 
                          tool === 'youtube-reel' ? 'YouTube Reel Creator' : tool
                        ).join(', ') || 'None'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-10">
              <p className="text-gray-600">No users found.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render analytics tab
  const renderAnalyticsTab = () => {
    if (loading) {
      return (
        <div className="text-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics data...</p>
        </div>
      );
    }

    return (
      <div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-indigo-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Total Users</h3>
                <p className="text-3xl font-bold text-gray-700">{stats.totalUsers}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-green-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Active Users</h3>
                <p className="text-3xl font-bold text-gray-700">{stats.activeUsers}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-full">
                <BarChart2 className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">Tool Usage</h3>
                <p className="text-3xl font-bold text-gray-700">{stats.toolUsage.youtubeReel}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Tool Usage Breakdown</h3>
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-indigo-600 bg-indigo-200">
                  YouTube Reel Creator
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-indigo-600">
                  {stats.toolUsage.youtubeReel} uses
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-indigo-200">
              <div style={{ width: '100%' }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500"></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render settings tab
  const renderSettingsTab = () => {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Feature Settings
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Toggle features on or off for all users
          </p>
        </div>
        
        <div className="px-4 py-5 sm:px-6">
          <ul className="divide-y divide-gray-200">
            {/* YouTube Reel Creator */}
            <li className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Video className="flex-shrink-0 h-6 w-6 text-indigo-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">YouTube Reel Creator</p>
                    <p className="text-sm text-gray-500">Extract segments from YouTube videos</p>
                  </div>
                </div>
                <button
                  onClick={() => handleFeatureToggle('youtube-reel', !featureSettings['youtube-reel']?.enabled)}
                  disabled={updatingFeature === 'youtube-reel'}
                  className={`flex items-center h-6 rounded-full w-11 p-1 transition-colors duration-300 focus:outline-none ${featureSettings['youtube-reel']?.enabled ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'}`}
                >
                  <span
                    className={`bg-white rounded-full h-4 w-4 transform transition-transform duration-300 ${featureSettings['youtube-reel']?.enabled ? 'translate-x-0' : '-translate-x-0'}`}
                  ></span>
                </button>
              </div>
              <div className="mt-2 ml-9">
                <div className="text-xs">
                  {updatingFeature === 'youtube-reel' ? (
                    <span className="text-gray-500">Updating...</span>
                  ) : featureSettings['youtube-reel']?.enabled ? (
                    <span className="text-green-600 flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" /> Feature is enabled
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center">
                      <XCircle className="h-3 w-3 mr-1" /> Feature is disabled
                    </span>
                  )}
                </div>
              </div>
            </li>
            
            {/* FormConnect */}
            <li className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <MessageSquare className="flex-shrink-0 h-6 w-6 text-indigo-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">FormConnect</p>
                    <p className="text-sm text-gray-500">WhatsApp integrated forms</p>
                  </div>
                </div>
                <button
                  onClick={() => handleFeatureToggle('form-connect', !featureSettings['form-connect']?.enabled)}
                  disabled={updatingFeature === 'form-connect'}
                  className={`flex items-center h-6 rounded-full w-11 p-1 transition-colors duration-300 focus:outline-none ${featureSettings['form-connect']?.enabled ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'}`}
                >
                  <span
                    className={`bg-white rounded-full h-4 w-4 transform transition-transform duration-300 ${featureSettings['form-connect']?.enabled ? 'translate-x-0' : '-translate-x-0'}`}
                  ></span>
                </button>
              </div>
              <div className="mt-2 ml-9">
                <div className="text-xs">
                  {updatingFeature === 'form-connect' ? (
                    <span className="text-gray-500">Updating...</span>
                  ) : featureSettings['form-connect']?.enabled ? (
                    <span className="text-green-600 flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" /> Feature is enabled
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center">
                      <XCircle className="h-3 w-3 mr-1" /> Feature is disabled
                    </span>
                  )}
                </div>
              </div>
            </li>
            
            {/* Social Media Content Generator */}
            <li className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Star className="flex-shrink-0 h-6 w-6 text-indigo-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Social Media Content Generator</p>
                    <p className="text-sm text-gray-500">AI-powered content creation for social media</p>
                  </div>
                </div>
                <button
                  onClick={() => handleFeatureToggle('social-media-content', !featureSettings['social-media-content']?.enabled)}
                  disabled={updatingFeature === 'social-media-content'}
                  className={`flex items-center h-6 rounded-full w-11 p-1 transition-colors duration-300 focus:outline-none ${featureSettings['social-media-content']?.enabled ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'}`}
                >
                  <span
                    className={`bg-white rounded-full h-4 w-4 transform transition-transform duration-300 ${featureSettings['social-media-content']?.enabled ? 'translate-x-0' : '-translate-x-0'}`}
                  ></span>
                </button>
              </div>
              <div className="mt-2 ml-9">
                <div className="text-xs">
                  {updatingFeature === 'social-media-content' ? (
                    <span className="text-gray-500">Updating...</span>
                  ) : featureSettings['social-media-content']?.enabled ? (
                    <span className="text-green-600 flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" /> Feature is enabled
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center">
                      <XCircle className="h-3 w-3 mr-1" /> Feature is disabled
                    </span>
                  )}
                </div>
              </div>
            </li>
            
            {/* Digital Identity Hub */}
            <li className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <User className="flex-shrink-0 h-6 w-6 text-indigo-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Digital Identity Hub</p>
                    <p className="text-sm text-gray-500">Digital business cards with QR codes</p>
                  </div>
                </div>
                <button
                  onClick={() => handleFeatureToggle('digital-identity', !featureSettings['digital-identity']?.enabled)}
                  disabled={updatingFeature === 'digital-identity'}
                  className={`flex items-center h-6 rounded-full w-11 p-1 transition-colors duration-300 focus:outline-none ${featureSettings['digital-identity']?.enabled ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'}`}
                >
                  <span
                    className={`bg-white rounded-full h-4 w-4 transform transition-transform duration-300 ${featureSettings['digital-identity']?.enabled ? 'translate-x-0' : '-translate-x-0'}`}
                  ></span>
                </button>
              </div>
              <div className="mt-2 ml-9">
                <div className="text-xs">
                  {updatingFeature === 'digital-identity' ? (
                    <span className="text-gray-500">Updating...</span>
                  ) : featureSettings['digital-identity']?.enabled ? (
                    <span className="text-green-600 flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" /> Feature is enabled
                    </span>
                  ) : (
                    <span className="text-red-600 flex items-center">
                      <XCircle className="h-3 w-3 mr-1" /> Feature is disabled
                    </span>
                  )}
                </div>
              </div>
            </li>
          </ul>
        </div>
        
        <div className="px-4 py-4 bg-gray-50 sm:px-6">
          <div className="flex items-center">
            <Database className="h-5 w-5 text-gray-500 mr-2" />
            <p className="text-sm text-gray-500">
              Connected to Firebase project: <span className="font-mono">kangleitools</span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'users':
        return renderUsersTab();
      case 'analytics':
        return renderAnalyticsTab();
      case 'settings':
        return renderSettingsTab();
      default:
        return renderUsersTab();
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Manage users, view analytics, and control platform settings.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar Navigation */}
        <div className="w-full md:w-64 bg-white rounded-lg shadow overflow-hidden">
          <nav className="divide-y divide-gray-200">
            <button
              onClick={() => setActiveTab('users')}
              className={`w-full flex items-center px-6 py-4 text-sm font-medium ${
                activeTab === 'users' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Users className={`mr-3 h-5 w-5 ${activeTab === 'users' ? 'text-indigo-600' : 'text-gray-400'}`} />
              Users
            </button>
            
            <button
              onClick={() => setActiveTab('analytics')}
              className={`w-full flex items-center px-6 py-4 text-sm font-medium ${
                activeTab === 'analytics' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <BarChart2 className={`mr-3 h-5 w-5 ${activeTab === 'analytics' ? 'text-indigo-600' : 'text-gray-400'}`} />
              Analytics
            </button>
            
            <button
              onClick={() => setActiveTab('settings')}
              className={`w-full flex items-center px-6 py-4 text-sm font-medium ${
                activeTab === 'settings' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Settings className={`mr-3 h-5 w-5 ${activeTab === 'settings' ? 'text-indigo-600' : 'text-gray-400'}`} />
              Settings
            </button>
          </nav>
          
          <div className="p-4 bg-gray-50">
            <div className="flex items-center">
              <div className="bg-indigo-100 p-2 rounded-full">
                <Users className="h-4 w-4 text-indigo-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-500">Logged in as Admin</p>
                <p className="text-xs text-gray-500 truncate">{currentUser?.email}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="flex-1">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
